import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getAccessToken } from './jwt-storage';
import { hydrateSession, loadCrmProfile } from './auth.reducer';
import { useAppDispatch, useAppSelector } from '@/store';
import { PageSkeleton } from '@/components/loading';
import { hasAnyAuthority } from './has-any-authority';
import { resolveDefaultHomePath } from './default-home-route';
function satisfiesRoles(auth, requiredRoles) {
    if (!requiredRoles?.length)
        return true;
    return requiredRoles.some((r) => auth.roles.includes(r));
}
function satisfiesPermissions(auth, requiredPermissions) {
    if (!requiredPermissions?.length)
        return true;
    return requiredPermissions.every((p) => hasAnyAuthority(auth.permissions, [p]));
}
function satisfiesAnyPermissions(auth, requiredAnyPermissions) {
    if (!requiredAnyPermissions?.length)
        return true;
    return hasAnyAuthority(auth.permissions, requiredAnyPermissions);
}
function authAllows(auth, props) {
    const hasRoles = Boolean(props.requiredRoles?.length);
    const hasAllPerms = Boolean(props.requiredPermissions?.length);
    const hasAnyPerms = Boolean(props.requiredAnyPermissions?.length);
    if (!hasRoles && !hasAllPerms && !hasAnyPerms) {
        return true;
    }
    /** Legacy: both role + all-permission lists → user must satisfy both. */
    if (hasRoles && hasAllPerms && !hasAnyPerms) {
        return satisfiesRoles(auth, props.requiredRoles) && satisfiesPermissions(auth, props.requiredPermissions);
    }
    const checks = [];
    if (hasRoles)
        checks.push(satisfiesRoles(auth, props.requiredRoles));
    if (hasAllPerms)
        checks.push(satisfiesPermissions(auth, props.requiredPermissions));
    if (hasAnyPerms)
        checks.push(satisfiesAnyPermissions(auth, props.requiredAnyPermissions));
    return checks.some(Boolean);
}
function SessionHydratingFallback() {
    return (_jsx("div", { style: { minHeight: '50vh' }, children: _jsx(PageSkeleton, { showToolbar: false, tableColumns: 4, tableRows: 6 }) }));
}
/**
 * Wraps route branches that require an authenticated user.
 * Optional role/permission checks apply to all nested routes when props are passed.
 */
export function ProtectedRoute(props) {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { session, hydrating, noAssignedRoles, crmProfileLoaded, crmProfileLoading } = useAppSelector((s) => s.auth);
    const hasToken = typeof window !== 'undefined' && Boolean(getAccessToken());
    useEffect(() => {
        if (!hasToken)
            return;
        void dispatch(hydrateSession());
    }, [hasToken, dispatch]);
    useEffect(() => {
        if (!hasToken || !session)
            return;
        if (!crmProfileLoaded && !crmProfileLoading) {
            void dispatch(loadCrmProfile());
        }
    }, [session, hasToken, crmProfileLoaded, crmProfileLoading, dispatch]);
    if (noAssignedRoles) {
        return _jsx(Navigate, { to: "/no-role-assigned", replace: true });
    }
    if (!session && !hasToken) {
        return _jsx(Navigate, { to: "/login", replace: true, state: { from: location.pathname } });
    }
    if (!session && (hydrating || hasToken)) {
        return _jsx(SessionHydratingFallback, {});
    }
    if (session && session.roles.length === 0) {
        return _jsx(Navigate, { to: "/no-role-assigned", replace: true });
    }
    if (session && !authAllows(session, props)) {
        return _jsx(Navigate, { to: "/unauthorized", replace: true });
    }
    return _jsx(Outlet, {});
}
/**
 * Per-route gate: logged-in users missing role/permission go to /unauthorized.
 */
export function RequireRole({ children, requiredRoles, requiredPermissions, requiredAnyPermissions, }) {
    const auth = useAppSelector((s) => s.auth.session);
    if (!auth) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    if (!authAllows(auth, { requiredRoles, requiredPermissions, requiredAnyPermissions })) {
        return _jsx(Navigate, { to: "/unauthorized", replace: true });
    }
    return _jsx(_Fragment, { children: children });
}
export function GuestOnly({ children }) {
    const { session, noAssignedRoles } = useAppSelector((s) => s.auth);
    if (noAssignedRoles) {
        return _jsx(Navigate, { to: "/no-role-assigned", replace: true });
    }
    if (session) {
        return _jsx(Navigate, { to: resolveDefaultHomePath(session), replace: true });
    }
    return _jsx(_Fragment, { children: children });
}
